import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Crown, Star, Shield, TrendingUp, Gift, Check } from "lucide-react";

export default function VipLanding() {
  const { user, loaded } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const preselectedPlan = params.get("plan");
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState("");
  const [error, setError] = useState("");

  useEffect(() => { api.get("/vip/packages").then((r) => setPackages(r.data || [])); }, []);

  // Scroll to preselected plan & highlight it
  useEffect(() => {
    if (preselectedPlan && packages.length > 0) {
      setTimeout(() => {
        const el = document.querySelector(`[data-testid*="vip-plan-${preselectedPlan}"]`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 200);
    }
  }, [preselectedPlan, packages]);

  const isAuth = loaded && !!user && user !== false;
  // Allow attempting checkout for any authenticated role; backend will
  // return a helpful error if the user hasn't created a profile yet.

  const handleBuy = async (pkgId) => {
    setError("");
    if (!loaded) { setError("Cargando sesión, intenta de nuevo en un segundo…"); return; }
    if (!isAuth) { nav(`/registro?role=companion&plan=${pkgId}`); return; }

    setLoading(pkgId);
    try {
      const { data } = await api.post("/vip/checkout", {
        package_id: pkgId,
        origin_url: window.location.origin,
      });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        setError("No se pudo iniciar el pago. Intenta de nuevo.");
        setLoading("");
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail === "Primero crea tu perfil") {
        setError("Primero crea tu perfil de Musa en el panel. Te llevo ahí…");
        setTimeout(() => nav("/dashboard"), 1800);
      } else if (e.response?.status === 401 || e.response?.status === 403) {
        setError("Necesitas crear una cuenta como Musa o Agencia para activar VIP.");
        setTimeout(() => nav("/registro?role=companion"), 1800);
      } else {
        setError(typeof detail === "string" ? detail : (e.message || "Error al iniciar pago"));
      }
      setLoading("");
    }
  };

  return (
    <div data-testid="vip-landing">
      {/* HERO with FREE WEEK banner */}
      <section
        className="relative py-24 md:py-32"
        style={{
          background:
            "radial-gradient(ellipse 60% 40% at 50% -10%, rgba(200,16,46,0.2), transparent 70%), linear-gradient(135deg, #0A0A0A 0%, #151515 100%)",
          borderBottom: "1px solid var(--border-accent)",
        }}
      >
        <div className="relative max-w-4xl mx-auto text-center px-6">
          <div className="overline mb-4">Planes y precios</div>
          <h1 className="font-serif text-5xl md:text-7xl font-light mb-6" style={{ color: "var(--text-primary)" }}>
            Sé una <em style={{ color: "var(--brand-primary)", fontStyle: "italic" }}>Musa VIP</em>
          </h1>

          <div
            className="inline-block px-8 py-5 mb-8 mx-auto"
            style={{
              background: "linear-gradient(135deg, rgba(212,175,55,0.18), rgba(200,16,46,0.15))",
              border: "1px solid var(--vip-gold)",
              borderRadius: "2px",
              boxShadow: "0 8px 32px rgba(212,175,55,0.15)",
            }}
            data-testid="free-week-hero"
          >
            <Gift size={28} strokeWidth={1.2} color="var(--vip-gold)" className="mx-auto mb-2" />
            <div className="font-serif text-2xl md:text-3xl" style={{ color: "var(--vip-gold)" }}>
              Primera semana <strong>GRATIS</strong>
            </div>
            <p className="text-sm mt-2" style={{ color: "var(--text-secondary)", lineHeight: 1.7 }}>
              Al crear tu perfil activas automáticamente <strong style={{ color: "var(--vip-gold)" }}>15 días de VIP sin costo</strong>.<br />
              No se te cobrará nada durante la primera semana.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-center items-center mt-4">
            <Link to="/registro?role=companion" className="btn-primary" data-testid="vip-signup-cta">
              Empezar ahora
            </Link>
            <span className="text-xs" style={{ color: "var(--text-muted)" }}>
              o desplázate para ver los planes completos ↓
            </span>
          </div>
        </div>
      </section>

      {/* Pricing tiles — above-the-fold visible */}
      <section className="max-w-6xl mx-auto px-6 py-20" data-testid="pricing-section">
        <div className="text-center mb-10">
          <div className="overline mb-3">Después de la semana gratis</div>
          <h2 className="font-serif text-4xl md:text-5xl font-light" style={{ color: "var(--text-primary)" }}>
            Planes <em style={{ color: "var(--brand-primary)", fontStyle: "italic" }}>disponibles</em>
          </h2>
          <p className="text-sm mt-3 max-w-xl mx-auto" style={{ color: "var(--text-secondary)" }}>
            Todos los planes son <strong style={{ color: "var(--text-primary)" }}>un solo pago</strong>. Sin suscripciones automáticas. Cancela cuando quieras.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="pricing-grid">
          {packages.map((p, i) => {
            const isHighlighted = preselectedPlan === p.id || (!preselectedPlan && i === 1);
            return (
            <div
              key={p.id}
              className="p-8 text-center relative"
              style={{
                background: "var(--bg-surface)",
                border: `1px solid ${isHighlighted ? "var(--vip-gold)" : "var(--border-accent)"}`,
                borderRadius: "2px",
                transform: isHighlighted ? "scale(1.04)" : "none",
                boxShadow: isHighlighted ? "0 8px 32px rgba(212,175,55,0.18)" : "none",
                transition: "all 0.3s ease",
              }}
              data-testid={`vip-plan-${p.id} vip-package-${p.id}`}
            >
              {isHighlighted && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 vip-badge" style={{ background: "var(--bg-base)" }}>
                  <Crown size={10} /> {preselectedPlan === p.id ? "TU PLAN" : "MÁS POPULAR"}
                </div>
              )}

              <div className="overline mb-3">{p.label}</div>
              <div className="font-serif text-6xl font-light" style={{ color: isHighlighted ? "var(--vip-gold)" : "var(--brand-primary)" }}>
                ${p.amount.toLocaleString("es-MX")}
              </div>
              <p className="text-xs uppercase tracking-[0.2em] mt-1" style={{ color: "var(--text-secondary)" }}>
                {p.currency} · pago único
              </p>
              <p className="text-sm mt-4" style={{ color: "var(--text-muted)" }}>
                {p.days} días de visibilidad VIP
              </p>

              <ul className="text-left text-sm mt-6 mb-8 flex flex-col gap-2" style={{ color: "var(--text-secondary)" }}>
                <li className="flex gap-2"><Check size={14} color="var(--brand-primary)" /> Perfil primero en galería</li>
                <li className="flex gap-2"><Check size={14} color="var(--brand-primary)" /> Sello VIP dorado</li>
                <li className="flex gap-2"><Check size={14} color="var(--brand-primary)" /> Destacado en home</li>
                <li className="flex gap-2"><Check size={14} color="var(--brand-primary)" /> Prioridad en búsquedas</li>
              </ul>

              <button
                onClick={() => handleBuy(p.id)}
                disabled={loading === p.id || !loaded}
                className="btn-primary w-full inline-flex justify-center"
                data-testid={`vip-plan-cta-${p.id}`}
              >
                {loading === p.id
                  ? "Redirigiendo a Mercado Pago…"
                  : !loaded
                  ? "Cargando…"
                  : isAuth
                  ? `Activar ${p.label}`
                  : "Crear cuenta y activar"}
              </button>
            </div>
          );})}
        </div>

        <p className="text-center text-xs mt-10" style={{ color: "var(--text-muted)" }}>
          Pagos procesados por <strong style={{ color: "var(--text-secondary)" }}>Mercado Pago</strong> ·
          Acepta tarjeta, SPEI y OXXO · Sin cargos recurrentes
        </p>
        {error && (
          <p className="text-center text-xs mt-3" style={{ color: "#ef6d6d" }} data-testid="vip-landing-error">
            {error}
          </p>
        )}
      </section>

      {/* Boost promo + Benefits */}
      <section className="max-w-6xl mx-auto px-6 pb-24">
        <div
          className="p-8 md:p-10 mb-16 flex flex-col md:flex-row items-center justify-between gap-6"
          style={{
            background: "linear-gradient(135deg, rgba(200,16,46,0.14), rgba(127,29,29,0.08))",
            border: "1px solid var(--brand-primary)",
            borderRadius: "2px",
          }}
          data-testid="boost-banner"
        >
          <div>
            <div className="overline mb-2">Mini-promoción</div>
            <h3 className="font-serif text-3xl" style={{ color: "var(--text-primary)" }}>
              Boost 24 horas · <span style={{ color: "var(--brand-primary)" }}>$99 MXN</span>
            </h3>
            <p className="text-sm mt-2 max-w-md" style={{ color: "var(--text-secondary)" }}>
              ¿No listo para VIP? Prueba 24 horas al inicio de la galería por sólo $99. Ideal para
              fin de semana o fechas importantes.
            </p>
          </div>
          <Link to="/registro?role=companion" className="btn-secondary" data-testid="boost-cta">
            Conocer Boost
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { icon: <Star strokeWidth={1.3} />, title: "Visibilidad premium", desc: "Aparece primero en la galería y en la página principal." },
            { icon: <TrendingUp strokeWidth={1.3} />, title: "Más contactos", desc: "En promedio, las Musas VIP reciben 5x más mensajes." },
            { icon: <Shield strokeWidth={1.3} />, title: "Sello de calidad", desc: "El badge dorado transmite confianza y prestigio." },
          ].map((f) => (
            <div key={f.title} className="p-8" style={{ background: "var(--bg-surface)", border: "1px solid var(--border-subtle)" }}>
              <div style={{ color: "var(--vip-gold)" }}>{f.icon}</div>
              <h3 className="font-serif text-2xl mt-5 mb-2" style={{ color: "var(--text-primary)" }}>{f.title}</h3>
              <p className="text-sm" style={{ color: "var(--text-secondary)" }}>{f.desc}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
